/* eslint-disable react-hooks/rules-of-hooks */
import { useQuery } from 'react-query';
import { transformEventsToAppointment } from '../data/AppointmentEventRules';
import {
  QUERY_CONFIG_APPOINTMENT,
  QUERY_CONFIG_DEFAULT,
  QUERY_CONFIG_DISABLE_CACHE,
  QUERY_TYPE,
  QUERY_URL_PATH,
  SCHEDULABLE_STATES,
} from '../data/Constants';
import AxiosService from '../services/AxiosService';
import {
  handleAppointmentSearch,
  verifyAppointmentResults,
} from './AppointmentFn';
import { cleanEventsObjectForAPI } from './EventFn';

const {
  LOCATIONS,
  PURPOSES,
  PROVIDERS,
  SEARCH,
  SEARCH_V2,
  SEARCH_V3,
  BOOK,
  HOLD,
  REQUEST,
  HEARTBEAT,
} = QUERY_TYPE;

/**
 * Get a valid url based off of the config and query type
 * @param {string} type
 * @returns string
 */
export const getUrl = (type) => `${QUERY_URL_PATH[type]}`;

/**
 * Prepare an array of strings for a url query.
 * @param {array} params - array of param strings that need to be combined.
 * @returns prepared query string
 */
const prepQueryParams = (params = []) => {
  if (params.length <= 0) return '';
  const queryString = params.join('&');
  return `?${queryString}`;
};

/**
 * Return the useQuery object for providers
 *
 * @returns useQuery object
 */
export const fetchProviders = (props = {}) =>
  useQuery(PROVIDERS, () => _fetchProviders(props), QUERY_CONFIG_DEFAULT);

/**
 * Return the useQuery object for purposes
 *
 * @param {string} apptType
 * @returns useQuery object
 */
export const fetchPurposes = (props = {}) =>
  useQuery(PURPOSES, () => _fetchPurposes(props), QUERY_CONFIG_DEFAULT);

/**
 * Return the useQuery object for locations
 *
 * @param {object} config
 * @returns useQuery object
 */
export const fetchLocations = (props = {}) =>
  useQuery(LOCATIONS, () => _fetchLocations(props), QUERY_CONFIG_DEFAULT);

/**
 * Return the useQuery object for the heartbeat
 *
 * @param {object} config
 * @returns useQuery object
 */
export const fetchHeartbeat = () =>
  useQuery(HEARTBEAT, async () => _fetchHeartbeat(), {
    ...QUERY_CONFIG_DEFAULT,
    ...QUERY_CONFIG_DISABLE_CACHE,
  });

/**
 * private version of fetch providers that does not use useQuery
 * axios will throw any non-200 errors to the catch block
 *
 * @param {any} config
 * @returns {any}
 */
export const _fetchProviders = async (props = {}) => {
  const { purposeType = 'Medical', selectedState = '' } = props;
  try {
    const url = getUrl(PROVIDERS);
    let params = [];

    // Append the provider type flag
    const providerTypeQuery =
      purposeType === 'Cosmetic' ? 'cosmetic' : 'genderm';
    params.push(`ProviderType=${providerTypeQuery}`);

    // Check for selected state
    if (selectedState !== '') params.push(`State=${selectedState}`);

    // Combine the new prepped params.
    const preppedParams = prepQueryParams(params);
    const { data } = await AxiosService.get(`${url}${preppedParams}`);
    return data.results;
  } catch (error) {
    throw error;
  }
};

/**
 * private version, re-usable in other areas (like searching for appointments)
 * @param {any} apptType
 * @param {boolean} expandAlternatives - Whether to request expanded alternatives from the API
 * @returns {any}
 */
export const _fetchPurposes = async ({
  purposeType = 'Medical',
  selectedState = 'IL',
  expandAlternatives = true, // Default to requesting expanded alternatives
}) => {
  try {
    const url = getUrl(PURPOSES);

    // Include expandAlternatives parameter if true
    const params = {
      State: selectedState,
      ...(expandAlternatives ? { expandAlternatives: 1 } : {})
    };

    const { data } = await AxiosService.get(`${url}`, { params });
    const { results } = data;

    if (!results) throw new Error('No Purposes returned by API');

    // Backend has already expanded alternatives if expandAlternatives=1 was used
    // Just filter by purpose type
    // For the "I have a different purpose" option, use the correct UNSPECIFIED purpose ID
    // based on whether this is a Medical or Cosmetic appointment
    const { UNSPECIFIED_MEDICAL, UNSPECIFIED_COSMETIC } = require('../data/Constants').APPT_PURPOSES;

    // Determine the correct unspecified purpose ID based on the purpose type
    const unspecifiedPurposeId = purposeType === 'Cosmetic' ? UNSPECIFIED_COSMETIC : UNSPECIFIED_MEDICAL;

    const purposes = [
      ..._filterPurposes(results, purposeType),
      {
        id: unspecifiedPurposeId,
        PurposeId: unspecifiedPurposeId,
        Title: 'I have a different purpose',
        EstablishedMedical: 'true',
        NewMedical: 'true',
        Cosmetic: 'true',
      },
    ];

    return purposes;
  } catch (error) {
    // Log the original error with more context
    console.error(`Error fetching purposes with expandAlternatives=${expandAlternatives}:`, error);
    // If the expanded API fails, try again without expandAlternatives
    // This ensures backward compatibility with older backend versions
    if (expandAlternatives) {
      try {
        console.warn('Attempting fallback to legacy purposes API endpoint without expandAlternatives');
        return await _fetchPurposes({
          purposeType,
          selectedState,
          expandAlternatives: false
        });
      } catch (fallbackError) {
        // Log the fallback error with context
        console.error('Critical error: Both expanded and legacy purposes API calls failed:', fallbackError);
        // Don't silently fail - propagate a clear error with context
        throw new Error(`Failed to fetch purposes: ${fallbackError.message} (after fallback attempt)`);
      }
    }

    // If we're already in the fallback case (expandAlternatives=false), propagate the error
    throw error;
  }
};

/**
 * adds an appointment id
 * filters based on the current type
 * sorts alpha
 *
 * @param {any} purposes
 * @param {any} apptType
 * @returns {any}
 */
const _filterPurposes = (purposes, apptType) => {
  return purposes
    .map((p) => {
      return { id: p.PurposeId, ...p };
    })
    .filter((p) => p[apptType] === 'true')
    .sort((p1, p2) => (p1.Title > p2.Title ? 1 : -1));
};

export const _fetchLocations = async (props = {}) => {
  const { zipCode = '', selectedState = '' } = props;
  try {
    const url = getUrl(LOCATIONS);
    let params = [];

    const useState = selectedState !== '' ? selectedState : '';

    params.push(`State=${useState}`);
    if (zipCode !== '') params.push(`zip=${zipCode}`);

    const preppedParams = prepQueryParams(params);

    const { data } = await AxiosService.get(`${url}${preppedParams}`);
    return data.results;
  } catch (error) {
    throw error;
  }
};

const _fetchHeartbeat = async () => {
  try {
    const { data } = await AxiosService.get(getUrl(HEARTBEAT));
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * doing the mapping here to transform the data to make the appointment request.
 * I'm a little confused on the best place to do this transform. To run this, I
 * need a list of all providers, all locations Then the transformation needs to
 * happen to turn the events into an appointment request
 *
 * @param {any} config
 * @param {any} events
 * @returns {any}
 */
export const fetchAppointments = async (props = {}) => {
  const { apiVersion } = props;

  // Switch to a new URI but use the same function for v3 -> v2
  const SEARCH_URI = Number(apiVersion) == 3 ? SEARCH_V3 : SEARCH_V2;

  switch (Number(apiVersion)) {
    case 1:
      return await fetchAppointments_V1(props);
    case 2:
    case 3:
    default:
      return await fetchAppointments_V2({ ...props, SEARCH_URI });
  }
};

export const fetchAppointments_V1 = (props) => {
  const { events, patientOrOther, purposeType } = props;
  return useQuery(
    SEARCH,
    async () => {
      try {
        const { result: stateResult = false } = events.find(
          ({ id }) => id === 'select-your-state',
        );
        const stateObj = SCHEDULABLE_STATES.find(
          ({ long }) =>
            `${long}`.toLowerCase() === `${stateResult}`.toLowerCase(),
        );

        const defaultSearchProps = {
          selectedState: stateObj.short,
        };

        const apiEvents = cleanEventsObjectForAPI({ events, patientOrOther });
        const locationResults = await _fetchLocations({
          ...defaultSearchProps,
        });
        const locations = locationResults.map((loc) => loc.LocationId);
        const providerResults = await _fetchProviders({
          purposeType,
          ...defaultSearchProps,
        });
        const providers = providerResults.map((prov) => prov.ProviderId);

        const searchParams = transformEventsToAppointment({
          locations,
          providers,
        })(apiEvents);

        // here is another version of events, except this one does not scrub results of metadata
        const Events = cleanEventsObjectForAPI({
          events,
          patientOrOther,
          doNotScrubResult: true,
        });

        const { data } = await AxiosService.post(getUrl(SEARCH), {
          ...searchParams,
          Events,
        });

        const { results } = data;

        // Check if results is undefined or not.  If it is, then we most likely got a 204
        // with an error in the search returning nothing.
        return typeof results === 'undefined'
          ? []
          : verifyAppointmentResults(results);
      } catch (error) {
        throw error;
      }
    },
    QUERY_CONFIG_APPOINTMENT,
  );
};

export const fetchAppointments_V2 = async (props) => {
  const {
    events,
    patientOrOther,
    purposeType,
    setStatus = () => {},
    setApptOptions = () => [],
    SEARCH_URI = SEARCH_V2,
    apiVersion,
  } = props;
  let searchType = 'exact';

  try {
    const { result: stateResult = false } = events.find(
      ({ id }) => id === 'select-your-state',
    );

    const selectedState = SCHEDULABLE_STATES.find(
      ({ long }) => `${long}`.toLowerCase() === `${stateResult}`.toLowerCase(),
    ).short;

    // Fetch all locations
    const locationResults = await _fetchLocations({
      selectedState,
    });

    // Get the location ID's
    const locations = locationResults.map((loc) => loc.LocationId);

    // Fetch all providers
    const providerResults = await _fetchProviders({
      purposeType,
      selectedState,
    });

    // Get the provider ID's
    const providers = providerResults.map((prov) => prov.ProviderId);

    let returnResults = [];

    returnResults = await handleAppointmentSearch({
      apiVersion,
      locations,
      providers,
      events,
      patientOrOther,
      setStatus,
      AxiosService,
      SEARCH_URI,
      setApptOptions,
    });

    if (returnResults.length === 0) {
      searchType = 'modified';

      // Determine if the locations or providers have been selected, and if
      // we should even attempt to expand the search.
      const eventsToFilter = events.filter(({ id, result = [] }) =>
        (id === 'select-location' || id === 'select-provider') &&
        result !== null &&
        result?.length > 0
          ? true
          : false,
      );

      // Continue if either have been provided filters
      if (eventsToFilter.length > 0) {
        returnResults = await handleAppointmentSearch({
          apiVersion,
          locations,
          providers,
          events,
          patientOrOther,
          setStatus,
          AxiosService,
          SEARCH_URI,
          setApptOptions,
          overrideFilters: true,
        });
      }
    }

    // Sort the results then trim the array down to 10.
    returnResults = returnResults
      .sort((itm1, itm2) => itm1.ApptStartTimeStamp - itm2.ApptStartTimeStamp)
      .splice(0, 10);

    return {
      type: searchType,
      appointments: returnResults,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * holds an appointment, does not require a payload to be sent as the held appt is
 * the guid
 * @param {any} appointment
 * @returns {any}
 */
export const holdAppointment = (appointment) => {
  const { appointmentId, Events } = appointment;
  const url = getUrl(HOLD).replace('#id', appointmentId);
  return AxiosService.put(url, { Events });
};

/**
 * adds further information to the appointment to reserve it for the patient until
 * linked
 *
 * @param {any} appointment
 * @returns {any}
 */
export const bookAppointment = (appointment) => {
  const {
    OfferId,
    FirstName,
    LastName,
    BirthDate,
    Address1,
    Address2,
    City,
    State,
    Zip,
    Sex,
    InsuranceCompany,
    InsuranceGroupNumber,
    InsuranceMemberId,
    InsurancePoBox,
    Phone,
    Email,
    Message,
    Events,
    Translator,
    Capable,
    Guardians,
    Language,
    HearAboutUsOption,
    SubHearAboutUsOption,
    MessagingConsent,
  } = appointment;

  return AxiosService.put(getUrl(BOOK).replace('#id', OfferId), {
    FirstName,
    LastName,
    BirthDate,
    Address1,
    Address2,
    City,
    State,
    Zip,
    Sex,
    InsuranceCompany,
    InsuranceGroupNumber,
    InsuranceMemberId,
    InsurancePoBox,
    Phone,
    Email,
    Message,
    Events,
    Translator,
    Capable,
    Guardians,
    Language,
    HearAboutUsOption,
    SubHearAboutUsOption,
    // Must be named here AND in the destructure above: this function whitelists
    // by re-listing, so a key missing from either is silently dropped.
    MessagingConsent,
  });
};

/**
 * Request an appointment
 * @param {any} appointment
 * @returns {any}
 */
export const requestAppointment = (appointment) => {
  const {
    FirstName,
    LastName,
    BirthDate,
    Address1,
    Address2,
    City,
    State,
    Zip,
    Sex,
    InsuranceCompany,
    InsuranceGroupNumber,
    InsuranceMemberId,
    InsurancePoBox,
    Phone,
    Email,
    Message,
    Events,
    Translator,
    Capable,
    Guardians,
    Language,
    HearAboutUsOption,
    SubHearAboutUsOption,
    MessagingConsent,
  } = appointment;

  return AxiosService.put(getUrl(REQUEST), {
    FirstName,
    LastName,
    BirthDate,
    Address1,
    Address2,
    City,
    State,
    Zip,
    Sex,
    InsuranceCompany,
    InsuranceGroupNumber,
    InsuranceMemberId,
    InsurancePoBox,
    Phone,
    Email,
    Message,
    Events,
    Translator,
    Capable,
    Guardians,
    Language,
    HearAboutUsOption,
    SubHearAboutUsOption,
    MessagingConsent,
  });
};
